import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, firstValueFrom, of, pipe, switchMap, tap } from 'rxjs';
import { ActiveUserState } from '../user/active-user.state';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../services/account/auth.service';
import { Router } from '@angular/router';
import { HyperionContext } from '../../models/base-entity';
import { DialogService } from 'primeng/dynamicdialog';
import { LoginFormComponent } from '../../components/molecules/login-form/login-form.component';

export function withAuthenticationOperations() {
  return signalStoreFeature(
    {
      state: type<ActiveUserState>(),
    },
    withMethods(
      (
        store,
        authService = inject(AuthService),
        router = inject(Router),
        dialogService = inject(DialogService)
      ) => ({
        // Triggered when login is required
        openLoginDialog(headerText = 'Ihr Login ist abgelaufen') {
          if (!store.dialogRef()) {
            patchState(store, {
              dialogRef: dialogService.open(LoginFormComponent, {
                header: headerText,
                width: '33rem',
                closable: false,
              }),
            });
          }
        },

        // Closes the dialog (e.g., after successful login)
        closeLoginDialog() {
          store.dialogRef()?.close();
          patchState(store, {
            dialogRef: undefined,
          });
        },

        checkAuthStatus(bypassRedirect = false): Promise<boolean> {
          const currentUrl = window.location.pathname;

          // Define routes to bypass authentication check
          const BLACKLISTED_ROUTE_PREFIXES: string[] = [
            '/atlas-intern',
            '/data-backend',
            '/dashboard',
          ];

          const requiresAuthCheck = BLACKLISTED_ROUTE_PREFIXES.some(prefix =>
            currentUrl.startsWith(prefix)
          );

          if (!requiresAuthCheck) return Promise.resolve(true);

          patchState(store, { loading: true, error: null });
          return firstValueFrom(
            authService.checkAuthStatus().pipe(
              tap(response => {
                // Update state if authenticated successfully
                patchState(store, {
                  isLoggedIn: true,
                  user: response.user,
                  loading: false,
                  error: null,
                  initialized: true,
                  commune: response.user.commune,
                });

                this.closeLoginDialog(); // Close dialog after success
              }),
              catchError(error => {
                console.error('Authentication check failed:', error);

                // Handle unauthenticated state
                patchState(store, {
                  isLoggedIn: false,
                  loading: false,
                  error: {
                    status: error.status,
                    message:
                      'Login abgelaufen. Bitte melden Sie sich erneut an.',
                  },
                });

                // Redirect to login or handle dialog
                //if (!bypassRedirect) router.navigate(['/login']);
                this.openLoginDialog('Login abgelaufen');

                // Rethrow error or return `false` for failed state
                return of(false);
              })
            )
          );
        },
        isLoginActive(): Promise<boolean> {
          return firstValueFrom(
            authService.checkAuthStatus().pipe(
              tap(response => {
                // Update state if authenticated successfully
                patchState(store, {
                  isLoggedIn: true,
                  user: response.user,
                  loading: false,
                  error: null,
                  initialized: true,
                  commune: response.user.commune,
                });
                setTimeout(() => {
                  router.navigate(['/dashboard']);
                }, 300);
              }),
              catchError(error => {
                patchState(store, {
                  isLoggedIn: false,
                  loading: false,
                });

                return of(false);
              })
            )
          );
        },

        setContext(context: HyperionContext) {
          patchState(store, { context });
        },
        setPublicCommune(commune: string, slug: string) {
          patchState(store, { commune, slug, context: 'atlasExternal' });
        },
        login: rxMethod<{ username: string; password: string }>(
          pipe(
            switchMap(({ username, password }) => {
              patchState(store, { loading: true, error: null });
              return authService.login({ username, password }).pipe(
                tapResponse({
                  next: response => {
                    store.dialogRef()?.close();

                    patchState(store, {
                      isLoggedIn: true,
                      user: response.user,
                      loading: false,
                      error: null,
                      initialized: true,
                      dialogRef: undefined,
                      commune: response.user.commune,
                    });

                    if (router.url === '/' || router.url === '/login') {
                      setTimeout(() => {
                        router.navigate(['/dashboard']);
                      }, 300);
                    }
                  },
                  error: (err: HttpErrorResponse) => {
                    const errorMessage =
                      err.status === 404
                        ? 'Benutzer nicht gefunden'
                        : err.message || 'An unknown error occurred';
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      error: { status: err.status, message: errorMessage },
                      commune: '',
                      initialized: true,
                    });
                  },
                })
              );
            })
          )
        ),
        logout: rxMethod<{ redirect?: boolean } | void>(
          pipe(
            switchMap((args = { redirect: true }) => {
              const redirect =
                args && 'redirect' in args ? args.redirect : true;

              patchState(store, { loading: true });

              return authService.logout().pipe(
                tapResponse({
                  next: () => {
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      commune: '',
                      initialized: false,
                    });

                    // Use the redirect parameter now
                    if (redirect) {
                      router.navigate(['/login']);
                    }
                  },
                  error: (err: HttpErrorResponse) => {
                    const errorMessage =
                      err.message || 'An unknown error occurred';
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      error: { status: err.status, message: errorMessage },
                      commune: '',
                      initialized: false,
                    });
                  },
                })
              );
            })
          )
        ),
        loginRedirect(): void {
          router.navigate(['/login']);
        },
      })
    )
  );
}
